/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * useProfileComplete — drives the onboarding profile gate. Subscribes to
 * /users/{uid}/profile/displayName for the signed-in user and reports whether
 * the user has set a REAL display name (i.e. not the "MyAmbits User" fallback
 * that ensureUserProfile seeds on first sign-in).
 *
 * Phone-only onboarding writes the user's chosen name to that same field, so a
 * non-placeholder displayName is the signal that the profile step is done.
 * Photo is optional and never gates completion.
 *
 * Mirrors useVerifiedPhone: returns {complete:false, loading:true} on cold
 * boot, so the gate must check `loading` before rendering the screen to avoid
 * flashing it for a returning user whose snapshot hasn't landed yet.
 */

import { useEffect, useState } from "react";
import { getDatabase, onValue, ref } from "firebase/database";
import { useAuth } from "@/contexts/AuthContext";
import { isPlaceholderDisplayName } from "@/utils/username";

export interface UseProfileCompleteReturn {
  complete: boolean;
  loading: boolean;
}

export function useProfileComplete(): UseProfileCompleteReturn {
  const { user } = useAuth();
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setComplete(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const db = getDatabase();
    const unsub = onValue(
      ref(db, `users/${user.uid}/profile/displayName`),
      (snap) => {
        const value = snap.val();
        const name = typeof value === "string" ? value : null;
        // Complete = a real, user-chosen name (not the seeded fallback). Email
        // is irrelevant in phone-only, so pass null for the relay heuristic.
        setComplete(!isPlaceholderDisplayName(name, null));
        setLoading(false);
      },
      (err) => {
        console.warn("[useProfileComplete] read failed:", err.message);
        setComplete(false);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [user]);

  return { complete, loading };
}
